import React from 'react';
import { useSelector } from 'react-redux';
import Table from 'antd/es/table';
import { CaretDownOutlined, CaretRightOutlined } from '@ant-design/icons';
import { tableFiltersAtom, searchTextAtom, sortByAtom } from '../../atoms';
import { useAtom, useAtomValue } from 'jotai';
import { getFilteredAlerts, getColumns, getExportColumns } from './utils';
import { ecoAIAlert2AccessSelector, featureSelector } from '@src/store/common/selectors';
import { useSummaryTableExport } from '../../hooks/useSummaryExport';
import { Wrapper } from '../styled';
import { shouldShowImpactedUniques } from '../../utils';
import { isAppModule } from '@src/utils/constants';
import { Empty } from 'antd';
import { globalFeatureNames } from '@src/utils/featureToggle/constants';
import { useGlobalFeature } from '@src/globalStore/globalFeatureFlagsStore';

const TableContainer = ({
  alerts = [],
  interval,
  emptyPlaceholder,
  serverSideFilter = false,
}: {
  alerts: any[];
  interval: any;
  emptyPlaceholder?: React.ReactNode;
  serverSideFilter?: boolean;
}) => {
  const enableDPIAlertPagination = useGlobalFeature(globalFeatureNames.DPI_ALERT_PAGINATION);
  const isPaginationEnabled = enableDPIAlertPagination && isAppModule;
  const isAlertPartnerAccount = useSelector(featureSelector('is_alert_forwarding_partner'));
  const ecoAIAlert2 = useSelector(ecoAIAlert2AccessSelector);
  const searchText = useAtomValue(searchTextAtom);
  const [sortBy, setSortBy] = useAtom(sortByAtom);
  const showImpactedUniques = shouldShowImpactedUniques(ecoAIAlert2);
  const columns = React.useMemo(
    () =>
      getColumns(
        searchText,
        isAlertPartnerAccount,
        showImpactedUniques,
        sortBy,
        isPaginationEnabled,
      ),
    [searchText, isAlertPartnerAccount, showImpactedUniques, sortBy, isPaginationEnabled],
  );
  const exportColumns = getExportColumns(isAlertPartnerAccount, showImpactedUniques);

  const filters = useAtomValue(tableFiltersAtom);

  const dataSource = serverSideFilter ? alerts : getFilteredAlerts({ alerts, filters });

  const handleChange = (_p, _f, sorter: any) => {
    // Handle both single sorter and array of sorters
    let activeSorter;

    if (Array.isArray(sorter)) {
      // Find the sorter with an order (the newly clicked column)
      activeSorter = sorter.find((s) => s.order) || sorter[0];
    } else {
      activeSorter = sorter;
    }

    if (activeSorter?.columnKey && activeSorter?.order) {
      // Normal case: column clicked with a valid sort order
      setSortBy({
        key: activeSorter.columnKey,
        order: activeSorter.order,
      });
    } else if (activeSorter?.columnKey && !activeSorter?.order) {
      // Third click - cycle back to ascend instead of removing sort
      setSortBy({
        key: activeSorter.columnKey,
        order: 'ascend',
      });
    } else {
      // Fallback: toggle current sort order
      const newOrder = sortBy.order === 'ascend' ? 'descend' : 'ascend';
      setSortBy({
        key: sortBy.key,
        order: newOrder,
      });
    }
  };

  useSummaryTableExport({
    alerts: dataSource,
    columns: exportColumns,
    interval: interval,
    sortBy,
  });

  return (
    <Wrapper>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="alertId"
        pagination={false}
        scroll={
          !isPaginationEnabled
            ? { y: 'calc(100vh - 280px)', x: 1400, scrollToFirstRowOnChange: true }
            : { x: 1400 }
        }
        style={{ fontSize: '12px' }}
        onChange={handleChange}
        locale={{
          emptyText: emptyPlaceholder ?? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No alerts found" />
          ),
        }}
        expandable={{
          childrenColumnName: 'alertEvents',
          indentSize: 0,
          expandIcon: ({ expanded, onExpand, record }) =>
            record.alertEvents?.length > 1 ? (
              expanded ? (
                <CaretDownOutlined onClick={(e) => onExpand(record, e)} />
              ) : (
                <CaretRightOutlined onClick={(e) => onExpand(record, e)} />
              )
            ) : null,
        }}
        expandIconColumnIndex={7}
      />
    </Wrapper>
  );
};

export default TableContainer;
